import React, {
    useState, useContext, useEffect, useRef,
} from 'react';
import { SuggestionsContext } from '../context/SuggestionsContext';

import AuthorAvatar from './page/AuthorAvatar';
import Comment from './Comment';
import CommentInput from './CommentsInput';
import PageUpdateSuggestions from './page/PageUpdateSuggestions';

import { pluralText } from '../utilities/wordUtilities';
import { scrollToComment } from '../utilities/scrollUtilities';

const CommentSubmitting = ({ isSubmitting }) => {
    if (!isSubmitting) return null;
    return (
        <div className="post-comment print-hide">
            <div className="flex-none">
                <AuthorAvatar userId={window.userId} tooltip="Commenting as you" customClass="post-comment-avatar left" noLink />
            </div>
            <div className="post-comment-content">
                <span className="gray loading">Submitting comment</span>
            </div>
        </div>
    );
};

const ContainerLabel = (props) => {
    const { suggestions } = useContext(SuggestionsContext);
    const containerLabel = suggestions && suggestions.length
        ? `${pluralText(props.commentsCount, 'comment')} and ${pluralText(suggestions.length, 'update request')}`
        : pluralText(props.commentsCount, 'comment');

    return (<h4 className="comments__label">{containerLabel}</h4>);
};

const CommentsAndSuggestionsContainer = ({ page }) => {
    const [pageId, setPageId] = useState(null);
    const [comments, setComments] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(null);
    const [errorText, setErrorText] = useState('');
    const activeRequest = useRef(null);

    useEffect(() => {
        if (page && page.id !== pageId) {
            setPageId(page.id);
        }
    }, [page, pageId]);

    useEffect(() => {
        const getComments = () => {
            setComments(null);
            if (!pageId) return;

            if (activeRequest.current) {
                activeRequest.current.abort();
                activeRequest.current = null;
            }

            activeRequest.current = $.get(`/api/teams/${window.teamId}/pages/${pageId}/comments`);

            return activeRequest.current
                .done(({ comments }) => setComments(comments))
                .catch(console.error)
                .always(() => scrollToComment());
        };

        getComments();

        return () => {
            if (activeRequest.current) {
                activeRequest.current.abort();
            }
        };
    }, [pageId]);

    const submitComment = (comment) => {
        setIsSubmitting(true);
        setErrorText('');
        const _token = window.csrfToken;

        return $.post(`/api/teams/${window.teamId}/pages/${pageId}/comments`, { comment, _token })
            .done(({ success, comment: newComment }) => {
                if (success && newComment) {
                    setComments(prevComments => [...prevComments, newComment]);
                }
            })
            .catch((error) => {
                console.error(error);
                setErrorText('Error submitting comment, please check connection and try again');
            })
            .always(() => setIsSubmitting(false));
    };

    const deleteComment = (commentId) => {
        $.post(`/api/teams/${window.teamId}/pages/${pageId}/comments/${commentId}`, { _method: 'DELETE' })
            .catch(console.error);
        setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
    };

    if (!(page && page.id)) {
        return null;
    }

    if (!comments) {
        return (
            <div className="mt3">
                <span className="loading gray">Loading comments...</span>
            </div>
        );
    }

    return (
        <div className="mt3">
            <ContainerLabel commentsCount={comments.length} />
            <div className="post-comments">
                <PageUpdateSuggestions page={page} />
                {comments.map(comment => (
                    <Comment key={comment.id} comment={comment} deleteComment={deleteComment} />
                ))}
                {errorText && <div className="error italic red mb1">{errorText}</div>}
                <CommentSubmitting isSubmitting={isSubmitting} />
                <CommentInput submitComment={submitComment} isSubmitting={isSubmitting} />
            </div>
        </div>
    );
};

export default CommentsAndSuggestionsContainer;
